import { ArrowRight, ClipboardCheck, Phone, Shirt, Timer, WalletCards } from "lucide-react";
import { Link } from "wouter";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CtaBand } from "@/components/site/CtaBand";
import { PageHero } from "@/components/site/PageHero";
import { SiteLayout } from "@/components/site/SiteLayout";
import { faqs } from "@/content/faqs";
import { SITE_DETAILS } from "@shared/site";

export default function PatientResources() {
  return (
    <SiteLayout>
      <PageHero
        currentPage="Patient Resources"
        eyebrow="Physical therapy questions, answered"
        title={<>What to know <em>before your visit.</em></>}
        description="A little clarity can make it easier to begin. Find answers about referrals, insurance, what to wear, your first appointment, one-on-one care, and what may come after physical therapy."
        aside={
          <div className="resource-quick-card">
            <ClipboardCheck size={24} />
            <strong>Need help now?</strong>
            <p>The office can answer questions specific to scheduling, insurance, paperwork, and your first visit.</p>
            <a href={SITE_DETAILS.phoneHref}><Phone size={16} /> {SITE_DETAILS.phoneDisplay}</a>
          </div>
        }
      />

      <section className="page-section resource-prep" aria-labelledby="prepare-title">
        <div className="container">
          <div className="section-heading centered">
            <p className="section-kicker">Prepare with confidence</p>
            <h2 id="prepare-title">Your first visit, at a glance.</h2>
          </div>
          <div className="prep-grid">
            <article><Shirt /><h3>Wear something comfortable</h3><p>Choose clothing that lets you move and gives access to the area being evaluated.</p></article>
            <article><Timer /><h3>Plan for 40–45 minutes</h3><p>Session length varies with the plan, but this is a helpful range for scheduling your day.</p></article>
            <article><WalletCards /><h3>Bring relevant information</h3><p>Have your identification, insurance information, and any requested paperwork or records available.</p></article>
          </div>
        </div>
      </section>

      <section className="page-section page-section-white faq-section" aria-labelledby="faq-title">
        <div className="container faq-layout">
          <div className="faq-intro">
            <p className="section-kicker">Frequently asked questions</p>
            <h2 id="faq-title">Answers for planning your care.</h2>
            <p>These FAQs preserve the topics from the previous CORE website with grammar and clarity updates. Insurance, legal-access, app, and program details may change; contact the office for information specific to you.</p>
            <Link className="button-primary" href="/contact">Contact the office <ArrowRight size={18} /></Link>
          </div>
          <Accordion type="single" collapsible className="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem value={`faq-${index}`} key={faq.question}>
                <AccordionTrigger>
                  <span className="faq-question-label">
                    <span className="faq-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                    <span>{faq.question}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent><p>{faq.answer}</p></AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CtaBand title="Still have a question? Let’s talk." body="The CORE office can help with scheduling, insurance questions, paperwork, billing, and deciding whom you should speak with next." />
    </SiteLayout>
  );
}
